import { PropsWithChildren } from "react"
import { Helmet } from "react-helmet"
import { Outlet, useMatch } from "react-router-dom"
import styled, { createGlobalStyle } from "styled-components"

import { useAppSelector } from "store/hooks"
import { contractorThemeSelector, verticalMenuOpenSelector } from "store/tempDataSlise"
import { BareRoutes } from "constants/BareRoutes"
import { ContractorThemes } from "constants/ContractorThemes"
import SubscriptionPlan from "types/enums/SubscriptionPlan"

import { UserRole } from "utils/checkRoleType"
import { useUserProfile } from "hooks/useUserProfile"
import { ContractorTopbar } from "components/topbars/ContractorTopbar"
import { EnterpriseTopbar } from "components/topbars/EnterpriseTopbar"
import { SalesManagerTopbar } from "components/topbars/SalesManagerTopbar"
import { MOBILE_BREAKPOINT } from "components/topbars/styled"
import { VendorTopbar } from "components/topbars/VendorTopbar"
import { WerehouseTopbar } from "components/topbars/WerehouseTopbar"

import useUserProfileSync from "./useUserProfileSync"

const GlobalStyle = createGlobalStyle<{ background: string }>`
  body {
    background: ${({ background }) => background};
    overflow-x: hidden;
  }
`

const Page = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
`

const Content = styled.div<{
  $paddingLeft: string
  $paddingRight: string
  $mobilePadding: string
}>`
  padding-left: ${({ $paddingLeft }) => $paddingLeft};
  padding-right: ${({ $paddingRight }) => $paddingRight};
  padding-bottom: 24px;
  transition: padding-left 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: ${({ $mobilePadding }) => $mobilePadding};
  }
`

const MainLayout = ({ children }: PropsWithChildren) => {
  useUserProfileSync()
  const { userProfile, userRole, isContractor, isEnterprise } = useUserProfile()
  const hasEPlusPlan = userProfile?.contractor?.subscription_plan === SubscriptionPlan.E_PLUS
  const canUseCustomTheme = hasEPlusPlan || isEnterprise
  const currentTheme = useAppSelector(contractorThemeSelector)
  const menuOpen = useAppSelector(verticalMenuOpenSelector)
  const fullReportMatch = useMatch(BareRoutes.FullIntelligenceReport)
  const shortReportMatch = useMatch(BareRoutes.ReportRedirectBySn)
  const isBareRoute = Boolean(fullReportMatch) || Boolean(shortReportMatch)

  const getBackgroundStyle = (canUseCustomTheme: boolean) => {
    if (canUseCustomTheme || !userProfile) {
      return currentTheme.backgroundGradient
    }
    return ContractorThemes.default.backgroundGradient
  }

  const setNecessaryTopbar = () => {
    if (isEnterprise) {
      return <EnterpriseTopbar />
    }
    if (isContractor) {
      return <ContractorTopbar />
    }
    if (userRole === UserRole.VendorAdmin) {
      return <VendorTopbar />
    }
    if (userRole === UserRole.WarehouseManager) {
      return <WerehouseTopbar />
    }
    if (userRole === UserRole.SalesManager) {
      return <SalesManagerTopbar />
    }
    return null
  }

  const isAuthScreen = !userProfile

  const getContentPaddingLeft = () => {
    if (isAuthScreen) return "0"
    return menuOpen ? "232px" : "76px"
  }

  const paddingLeft = getContentPaddingLeft()
  const paddingRight = isAuthScreen ? "0" : "24px"
  const mobilePadding = isAuthScreen ? "0" : "0 16px"

  if (isBareRoute) {
    return (
      <>
        <GlobalStyle background={getBackgroundStyle(canUseCustomTheme)} />
        <Helmet>
          <title>Nuve</title>
        </Helmet>
        {children}
        <Outlet />
      </>
    )
  }

  return (
    <>
      <GlobalStyle background={getBackgroundStyle(canUseCustomTheme)} />
      <Helmet>
        <title>Nuve</title>
      </Helmet>
      <Page>
        {setNecessaryTopbar()}
        <Content $paddingLeft={paddingLeft} $paddingRight={paddingRight} $mobilePadding={mobilePadding}>
          {children}
          <Outlet />
        </Content>
      </Page>
    </>
  )
}

export default MainLayout
